import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import usersStyles from '../components/dashboard/UsersSection.module.css';
import { adminListPlans } from '../../../api/planCatalogApi';
import { searchUsers, createUser, updateUser as updateAdminUser, deleteUser, revokeUserAccess, grantUserAccess, resetUserPassword } from '../../../api/adminApi';

const organizationTypeMap: Record<string, string[]> = {
  'Researchers & Students': ['academic institution', 'individual researcher'],
  'Startups': ['startup'],
  'Enterprises': ['corporation'],
  'Government': ['government agency'],
  'Other(NPO,Firms)': ['non-profit', 'law firm', 'consulting firm'],
};

const USER_TYPE_OPTIONS = [
  { value: 'individual researcher', label: 'Researcher / Student' },
  { value: 'academic institution', label: 'Academic Institution' },
  { value: 'startup', label: 'Startup' },
  { value: 'corporation', label: 'Enterprise / Corporation' },
  { value: 'government agency', label: 'Government Agency' },
  { value: 'law firm', label: 'Law Firm' },
  { value: 'consulting firm', label: 'Consulting Firm' },
  { value: 'non-profit', label: 'Non-profit / NGO' },
];

const getDefaultUserType = (category?: string | null, preferred?: string | null) => {
  const normalizedPreferred = (preferred || '').toLowerCase().trim();
  if (normalizedPreferred) return normalizedPreferred;

  switch (category) {
    case 'Researchers & Students':
      return 'individual researcher';
    case 'Startups':
      return 'startup';
    case 'Enterprises':
      return 'corporation';
    case 'Government':
      return 'government agency';
    case 'Other(NPO,Firms)':
      return 'non-profit';
    default:
      return 'individual researcher';
  }
};

const getDefaultPlanForUserType = (userType?: string | null) => {
  switch ((userType || '').toLowerCase().trim()) {
    case 'corporation':
      return 'enterprise';
    case 'startup':
      return 'startup';
    case 'government agency':
      return 'government_state';
    case 'law firm':
      return 'law_firm_firm';
    default:
      return '';
  }
};

// Plans come from the catalogue, not from a list here. The list here named
// startup, growth, law_firm_firm and two government tiers — none of which exist
// since KN replaced the plan set on 22 Sep — while omitting enterprise,
// government and free, which do. Setting a user to a tier the catalogue has
// never heard of gives them a subscription no entitlement can resolve.
const NO_PLAN = { value: '', label: 'Default (no subscription)' };

const inputStyle: React.CSSProperties = {
  padding: '8px 10px',
  borderRadius: 6,
  border: '1px solid var(--admin-border,var(--ds-border))',
  fontSize: 13,
  width: '100%',
  boxSizing: 'border-box',
};

const labelStyle: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 500,
  color: 'var(--admin-text-muted,var(--ds-text-muted))',
  marginBottom: 3,
  display: 'block',
};

const fieldStyle: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 3 };

const rowStyle: React.CSSProperties = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 };

const normalizeDateInput = (value?: string | null) => (value ? String(value).slice(0, 10) : '');

export default function UsersManagePage() {
  const location = useLocation();
  const navigate = useNavigate();
  const category = (location.state as any)?.category || new URLSearchParams(location.search).get('category') || 'All users';
  const pendingEditUserId = Number((location.state as any)?.editUserId || 0) || null;
  const shouldAutoOpenAdd = Boolean((location.state as any)?.openAdd);
  const initialUserType = getDefaultUserType(category, (location.state as any)?.initialUserType || null);
  const isEnterprise = category === 'Enterprises';

  // null = full access (default admin), string[] = custom role action list
  const allowedActions: string[] | null = (location.state as any)?.allowedActions ?? null;
  const canCreate = allowedActions === null || allowedActions.includes('create_user');
  const canEdit   = allowedActions === null || allowedActions.includes('edit_permissions');
  const canDelete = allowedActions === null || allowedActions.includes('delete_user');

  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingUser, setEditingUser] = useState<any | null>(null);
  // SRS #59: administrator lock on what the account may see.
  const [authJur, setAuthJur] = useState<string>('');
  const [authJurInfo, setAuthJurInfo] = useState<{ is_government: boolean; effective: string[] | null } | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 15;
  const [grantUserId, setGrantUserId] = useState<number | null>(null);
  const [grantDays, setGrantDays] = useState('14');

  // Form fields
  const [newEmail, setNewEmail] = useState('');
  const [newName, setNewName] = useState('');
  const [newUserType, setNewUserType] = useState(initialUserType);
  const [newOrgName, setNewOrgName] = useState('');
  const [newJobRole, setNewJobRole] = useState('');
  const [planOptions, setPlanOptions] = useState<{ value: string; label: string }[]>([NO_PLAN]);
  const [newPlanTier, setNewPlanTier] = useState(isEnterprise ? 'enterprise' : getDefaultPlanForUserType(initialUserType));
  const [newBillingCycle, setNewBillingCycle] = useState('');
  const [newPeriodStart, setNewPeriodStart] = useState('');
  const [newPeriodEnd, setNewPeriodEnd] = useState('');
  const [newSeats, setNewSeats] = useState('');
  const [newAmountPaid, setNewAmountPaid] = useState('');
  const [newAmountPending, setNewAmountPending] = useState('');
  const [newAdminNotes, setNewAdminNotes] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [autoOpenedForUserId, setAutoOpenedForUserId] = useState<number | null>(null);
  const [autoOpenedAdd, setAutoOpenedAdd] = useState(false);
  const isEditMode = editingUser !== null;
  const showEnterpriseFields = isEnterprise || newUserType === 'corporation' || (newPlanTier || '').toLowerCase() === 'enterprise';

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const token = window.localStorage.getItem('policyora_token') || '';
        if (!category || category === 'All users') {
          const all = await searchUsers(token);
          setUsers(all);
        } else if (category === 'Enterprises') {
          // Enterprises = corporation org type OR enterprise plan tier
          const all = await searchUsers(token, undefined, ['corporation'], 'enterprise');
          setUsers(all);
        } else {
          const orgTypes = organizationTypeMap[category] || [];
          const all = await searchUsers(token, undefined, orgTypes);
          if (category === 'Other(NPO,Firms)') {
            // Exclude those that are enterprise
            setUsers(all.filter(u => {
              const ot = (u.organization_type || '').toLowerCase().trim();
              const isEnt = ot === 'corporation' || (u.plan_tier || '').toLowerCase() === 'enterprise';
              return !isEnt;
            }));
          } else {
            setUsers(all);
          }
        }
      } catch {
        setError('Failed to load users');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [category]);

  /**
   * Generate a password that ALWAYS satisfies both the backend rule
   * (`_validate_password_strength` in `backend/app/api/v1/auth.py` —
   * needs ≥1 letter and ≥1 digit) and the Cognito User Pool policy
   * (needs ≥1 upper, ≥1 lower, ≥1 digit, ≥1 special).
   *
   * The previous implementation picked all characters uniformly at
   * random from the combined alphabet — perfectly capable of returning
   * a digit-free string like "u(x_@@r$SpD)", which the backend would
   * (correctly) reject with HTTP 400. That produced the opaque
   * "Failed to add user" alert in the admin UI even though the form
   * was filled in correctly.
   */
  const generatePassword = (len = 12) => {
    const upper = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const lower = 'abcdefghijklmnopqrstuvwxyz';
    const digit = '0123456789';
    const special = '!@#$%^&*()_+-=';
    const pick = (s: string) => s.charAt(Math.floor(Math.random() * s.length));

    // Guarantee one of each class first, then fill the rest from the
    // combined alphabet, then shuffle so the guaranteed characters
    // aren't always in positions 0..3.
    const chars: string[] = [pick(upper), pick(lower), pick(digit), pick(special)];
    const all = upper + lower + digit + special;
    while (chars.length < len) chars.push(pick(all));
    for (let i = chars.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [chars[i], chars[j]] = [chars[j], chars[i]];
    }
    return chars.join('');
  };

  const resetForm = useCallback(() => {
    const defaultUserType = initialUserType;
    setNewEmail('');
    setNewName('');
    setNewUserType(defaultUserType);
    setNewOrgName('');
    setNewJobRole('');
    setNewPlanTier(isEnterprise ? 'enterprise' : getDefaultPlanForUserType(defaultUserType));
    setNewBillingCycle('');
    setNewPeriodStart('');
    setNewPeriodEnd('');
    setNewSeats('');
    setNewAmountPaid('');
    setNewAmountPending('');
    setNewAdminNotes('');
    setNewPassword('');
    setShowPassword(false);
  }, [initialUserType, isEnterprise]);

  const closeForm = () => {
    setShowAddModal(false);
    setEditingUser(null);
    resetForm();
  };

  const handleAdd = () => {
    setEditingUser(null);
    resetForm();
    setShowAddModal(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    // One source of truth for which plans exist. A failure leaves only
    // "no subscription", which is honest: better than offering a retired tier.
    adminListPlans()
      .then((view) => {
        const plans = (view?.plans ?? []).filter((pl: any) => !pl.disabled);
        setPlanOptions([NO_PLAN, ...plans.map((pl: any) => ({
          value: pl.tier,
          label: pl.display_name || pl.tier,
        }))]);
      })
      .catch(() => setPlanOptions([NO_PLAN]));
  }, []);

  useEffect(() => {
    // A tier the catalogue no longer has cannot be shown by the select, so the
    // form would display "no subscription" while still holding, and then
    // saving, the retired value. Fall back to no subscription instead.
    if (planOptions.length <= 1) return;
    if (newPlanTier && !planOptions.some((o) => o.value === newPlanTier)) {
      setNewPlanTier('');
    }
  }, [planOptions, newPlanTier]);

  const handleEdit = useCallback((user: any) => {
    const resolvedUserType = getDefaultUserType(
      category,
      user.organization_type || (((user.plan_tier || '').toLowerCase() === 'enterprise') ? 'corporation' : null),
    );
    setEditingUser(user);
    setAuthJur(''); setAuthJurInfo(null);
    const lockToken = window.localStorage.getItem('policyora_token') || '';
    if (lockToken) {
      import('../../../api/adminApi').then((api) => api.getAuthorisedJurisdictions(lockToken, user.id))
        .then((v) => { setAuthJur((v.authorised_jurisdictions || []).join(', ')); setAuthJurInfo({ is_government: v.is_government, effective: v.effective }); })
        .catch(() => {});
    }
    setNewEmail(user.email || '');
    setNewName(user.full_name || user.name || '');
    setNewUserType(resolvedUserType);
    setNewOrgName(user.organization_name || '');
    setNewJobRole(user.job_role || '');
    setNewPlanTier((user.plan_tier || (resolvedUserType === 'corporation' ? 'enterprise' : getDefaultPlanForUserType(resolvedUserType)) || '').toLowerCase());
    setNewBillingCycle(user.billing_cycle || '');
    setNewPeriodStart(normalizeDateInput(user.period_start));
    setNewPeriodEnd(normalizeDateInput(user.period_end || user.trial_ends_at));
    setNewSeats(user.seats != null ? String(user.seats) : '');
    setNewAmountPaid(user.amount_paid != null ? String(user.amount_paid) : '');
    setNewAmountPending(user.amount_pending != null ? String(user.amount_pending) : '');
    setNewAdminNotes(user.admin_notes || '');
    setNewPassword('');
    setShowPassword(false);
    setShowAddModal(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [category]);

  const submitAdd = async () => {
    if (!newEmail) { window.alert('Please enter an email'); return; }
    if (showEnterpriseFields && !newPeriodEnd) { window.alert('Please enter the subscription valid until date'); return; }
    const pwd = newPassword || generatePassword();
    try {
      const token = window.localStorage.getItem('policyora_token') || '';
      const newUser = await createUser(token, {
        email: newEmail,
        full_name: newName || undefined,
        password: pwd,
        organization_type: newUserType || undefined,
        job_role: newJobRole || undefined,
        plan_tier: newPlanTier || undefined,
        organization_name: newOrgName || undefined,
        billing_cycle: newBillingCycle || undefined,
        period_start: newPeriodStart || undefined,
        period_end: newPeriodEnd || undefined,
        seats: newSeats ? parseInt(newSeats, 10) : undefined,
        amount_paid: newAmountPaid ? parseFloat(newAmountPaid) : undefined,
        amount_pending: newAmountPending ? parseFloat(newAmountPending) : undefined,
        admin_notes: newAdminNotes || undefined,
      });
      setUsers(prev => [newUser, ...prev]);
      closeForm();
    } catch (err) {
      // Surface the backend's actual error detail (e.g. password rules,
      // duplicate-email, Cognito failures). `parseResponse` attaches the
      // server's `detail` string as `err.message`, so empty fallback only
      // kicks in for network errors.
      const msg = err instanceof Error && err.message ? err.message : 'Failed to add user';
      window.alert(`Failed to add user: ${msg}`);
    }
  };

  const submitEdit = async () => {
    if (!editingUser) return;
    if (showEnterpriseFields && !newPeriodEnd) { window.alert('Please enter the subscription valid until date'); return; }
    try {
      const token = window.localStorage.getItem('policyora_token') || '';
      const updatedUser = await updateAdminUser(token, editingUser.id, {
        full_name: newName || null,
        organization_name: newOrgName || null,
        organization_type: newUserType || null,
        job_role: newJobRole || null,
        plan_tier: newPlanTier || null,
        billing_cycle: newBillingCycle || null,
        period_start: newPeriodStart || null,
        period_end: newPeriodEnd || null,
        seats: newSeats ? parseInt(newSeats, 10) : null,
        amount_paid: newAmountPaid ? parseFloat(newAmountPaid) : null,
        amount_pending: newAmountPending ? parseFloat(newAmountPending) : null,
        admin_notes: newAdminNotes || null,
      });
      setUsers(prev => prev.map(u => u.id === editingUser.id ? { ...u, ...updatedUser } : u));
      closeForm();
    } catch (err: any) {
      window.alert(err?.message || 'Failed to update user');
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete user?')) return;
    try {
      const token = window.localStorage.getItem('policyora_token') || '';
      await deleteUser(token, id);
      setUsers(prev => prev.filter(u => u.id !== id));
    } catch {
      window.alert('Failed to delete');
    }
  };

  const handleResetPassword = async (id: number, email: string) => {
    if (!window.confirm(
      `Reset the password for ${email}?\n\n` +
      'A new password will be generated and emailed to them. ' +
      'They will be signed out of any active sessions.'
    )) return;
    try {
      const token = window.localStorage.getItem('policyora_token') || '';
      const result = await resetUserPassword(token, id);
      // A generated password is returned once and cannot be retrieved again, so
      // show it even when the email went out — the email may not arrive.
      const shown = result.temporary_password
        ? `\n\nTemporary password: ${result.temporary_password}\n\nCopy it now — it cannot be shown again.`
        : '';
      window.alert(
        (result.email_sent
          ? `Password reset. The new password was emailed to ${result.email}.`
          : `Password reset, but the email could not be sent to ${result.email}.`) + shown
      );
    } catch (err: any) {
      window.alert(err?.message || 'Failed to reset password');
    }
  };

  const handleRevoke = async (id: number) => {
    if (!window.confirm('Revoke access for this user? Their account will remain but subscription will be cancelled.')) return;
    try {
      const token = window.localStorage.getItem('policyora_token') || '';
      await revokeUserAccess(token, id);
      setUsers(prev => prev.map(u => u.id === id ? { ...u, subscription_status: 'cancelled' } : u));
    } catch {
      window.alert('Failed to revoke access');
    }
  };

  // SRS #78: block = the account cannot sign in and every session ends now;
  // unlike Revoke, nothing about the plan changes.
  const handleBlockToggle = async (id: number, currentlyActive: boolean) => {
    const next = !currentlyActive;
    if (!window.confirm(next ? 'Unblock this user? They will be able to sign in again.'
                             : 'Block this user? They will be signed out everywhere and cannot sign in until unblocked.')) return;
    try {
      const token = window.localStorage.getItem('policyora_token') || '';
      const updated = await updateAdminUser(token, id, { is_active: next });
      setUsers(prev => prev.map(u => u.id === id ? { ...u, is_active: updated.is_active } : u));
    } catch {
      window.alert(next ? 'Failed to unblock user' : 'Failed to block user');
    }
  };

  const canGrantAccessForUser = (user: any) => {
    const status = String(user?.subscription_status || '').toLowerCase();
    if (status === 'expired' || status === 'cancelled' || status === 'past_due') return true;

    if (user?.trial_ends_at) {
      const trialEnd = new Date(user.trial_ends_at);
      return !Number.isNaN(trialEnd.getTime()) && trialEnd < new Date();
    }

    return false;
  };

  const handleGrantAccess = async () => {
    if (!grantUserId) return;
    const days = parseInt(grantDays, 10);
    if (!days || days < 1) { window.alert('Enter a valid number of days'); return; }
    try {
      const token = window.localStorage.getItem('policyora_token') || '';
      await grantUserAccess(token, grantUserId, days);
      const trialEndsAt = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toISOString();
      setUsers(prev => prev.map(u => u.id === grantUserId ? { ...u, subscription_status: 'trialing', trial_ends_at: trialEndsAt } : u));
      setGrantUserId(null);
    } catch {
      window.alert('Failed to grant access');
    }
  };

  useEffect(() => {
    if (shouldAutoOpenAdd && !autoOpenedAdd && !pendingEditUserId) {
      setEditingUser(null);
      resetForm();
      setShowAddModal(true);
      setAutoOpenedAdd(true);
    }
  }, [autoOpenedAdd, pendingEditUserId, resetForm, shouldAutoOpenAdd]);

  useEffect(() => {
    if (!pendingEditUserId || pendingEditUserId === autoOpenedForUserId || users.length === 0) return;
    const targetUser = users.find(u => u.id === pendingEditUserId);
    if (targetUser) {
      handleEdit(targetUser);
      setAutoOpenedForUserId(pendingEditUserId);
    }
  }, [autoOpenedForUserId, handleEdit, pendingEditUserId, users]);

  return (
    <div style={{ padding: '1.25rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <h2 style={{ margin: 0 }}>{category}</h2>
          <div style={{ color: 'var(--admin-text-muted,var(--ds-text-muted))' }}>Manage users in {category}</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className={usersStyles.modalBackButton} onClick={() => navigate(-1)}>← Back</button>
          {canCreate && <button className={usersStyles.userSegmentActionPrimary} onClick={handleAdd}>+ Add user</button>}
        </div>
      </div>

      {showAddModal && (
        <div
          style={{
            background: 'var(--admin-surface,#fff)',
            borderRadius: 12,
            padding: 20,
            marginBottom: 16,
            border: '1px solid var(--admin-border,var(--ds-border))',
            maxWidth: showEnterpriseFields ? 640 : 520,
            width: '100%',
            marginLeft: 'auto',
            marginRight: 'auto',
            boxSizing: 'border-box',
          }}
        >

          {/* Header */}
          <div style={{ marginBottom: 20 }}>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>
              {isEditMode ? (showEnterpriseFields ? 'Edit Enterprise User' : 'Edit User') : (showEnterpriseFields ? 'Add Enterprise User' : 'Add User')}
            </h3>
            <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--admin-text-muted,var(--ds-text-muted))' }}>
              {showEnterpriseFields
                ? (isEditMode ? 'Update enterprise account and subscription details' : 'Provision an enterprise account with subscription details')
                : 'Choose the user type and only the relevant fields will be shown.'}
            </p>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

            <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--admin-text-muted,var(--ds-text-faint))', borderBottom: '1px solid var(--admin-border,var(--ds-border))', paddingBottom: 4 }}>
              Account Details
            </div>

            <div style={rowStyle}>
              <div style={fieldStyle}>
                <label style={labelStyle}>Email *</label>
                <input value={newEmail} onChange={e => setNewEmail(e.target.value)} placeholder="user@company.com" style={{ ...inputStyle, background: isEditMode ? 'var(--ds-surface-2)' : 'var(--ds-surface)' }} disabled={isEditMode} />
              </div>
              <div style={fieldStyle}>
                <label style={labelStyle}>Full name</label>
                <input value={newName} onChange={e => setNewName(e.target.value)} placeholder="Full name" style={inputStyle} />
              </div>
            </div>

            <div style={rowStyle}>
              <div style={fieldStyle}>
                <label style={labelStyle}>User type</label>
                <select
                  value={newUserType}
                  onChange={e => {
                    const nextType = e.target.value;
                    setNewUserType(nextType);
                    setNewPlanTier(getDefaultPlanForUserType(nextType));
                  }}
                  style={inputStyle}
                >
                  {USER_TYPE_OPTIONS.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
              <div style={fieldStyle}>
                <label style={labelStyle}>Job role</label>
                <input value={newJobRole} onChange={e => setNewJobRole(e.target.value)} placeholder="e.g. Compliance Manager" style={inputStyle} />
              </div>
            </div>

            <div style={fieldStyle}>
              <label style={labelStyle}>{showEnterpriseFields ? 'Company / Organization name' : 'Organization name'}</label>
              <input value={newOrgName} onChange={e => setNewOrgName(e.target.value)} placeholder="Acme Corp" style={inputStyle} />
            </div>

            {showEnterpriseFields ? (
              <>
                <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--admin-text-muted,var(--ds-text-faint))', borderBottom: '1px solid var(--admin-border,var(--ds-border))', paddingBottom: 4, marginTop: 4 }}>
                  Subscription Details
                </div>

                <div style={rowStyle}>
                  <div style={fieldStyle}>
                    <label style={labelStyle}>Plan</label>
                    <select value={newPlanTier} onChange={e => setNewPlanTier(e.target.value)} style={inputStyle}>
                      {planOptions.map(option => (
                        <option key={option.value || 'default'} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </div>
                  <div style={fieldStyle}>
                    <label style={labelStyle}>Billing cycle</label>
                    <select value={newBillingCycle} onChange={e => setNewBillingCycle(e.target.value)} style={inputStyle}>
                      <option value="">Not set</option>
                      <option value="annual">Annual</option>
                      <option value="monthly">Monthly</option>
                    </select>
                  </div>
                </div>

                <div style={rowStyle}>
                  <div style={fieldStyle}>
                    <label style={labelStyle}>Valid from</label>
                    <input type="date" value={newPeriodStart} onChange={e => setNewPeriodStart(e.target.value)} style={inputStyle} />
                  </div>
                  <div style={fieldStyle}>
                    <label style={labelStyle}>Valid until *</label>
                    <input type="date" value={newPeriodEnd} onChange={e => setNewPeriodEnd(e.target.value)} style={inputStyle} />
                  </div>
                </div>

                <div style={rowStyle}>
                  <div style={fieldStyle}>
                    <label style={labelStyle}>Number of seats</label>
                    <input type="number" min={1} value={newSeats} onChange={e => setNewSeats(e.target.value)} placeholder="e.g. 25" style={inputStyle} />
                  </div>
                  <div style={fieldStyle}>
                    <label style={labelStyle} />
                  </div>
                </div>

                <div style={rowStyle}>
                  <div style={fieldStyle}>
                    <label style={labelStyle}>Amount paid (USD)</label>
                    <input type="number" min={0} step="0.01" value={newAmountPaid} onChange={e => setNewAmountPaid(e.target.value)} placeholder="e.g. 5000" style={inputStyle} />
                  </div>
                  <div style={fieldStyle}>
                    <label style={labelStyle}>Amount pending (USD)</label>
                    <input type="number" min={0} step="0.01" value={newAmountPending} onChange={e => setNewAmountPending(e.target.value)} placeholder="e.g. 2499" style={inputStyle} />
                  </div>
                </div>

                <div style={fieldStyle}>
                  <label style={labelStyle}>Admin notes (internal only)</label>
                  <textarea
                    value={newAdminNotes}
                    onChange={e => setNewAdminNotes(e.target.value)}
                    placeholder="e.g. Referred by sales team, custom contract terms..."
                    rows={3}
                    style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }}
                  />
                </div>
              </>
            ) : (
              <div style={fieldStyle}>
                <label style={labelStyle}>Plan</label>
                <select value={newPlanTier} onChange={e => setNewPlanTier(e.target.value)} style={inputStyle}>
                  {planOptions.map(option => (
                    <option key={option.value || 'default'} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
            )}

            {!isEditMode && (
              <>
                {showEnterpriseFields && (
                  <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--admin-text-muted,var(--ds-text-faint))', borderBottom: '1px solid var(--admin-border,var(--ds-border))', paddingBottom: 4, marginTop: 4 }}>
                    Access
                  </div>
                )}

                <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                  <div style={{ flex: 1, ...fieldStyle }}>
                    <label style={labelStyle}>Temporary password</label>
                    <input
                      value={newPassword}
                      onChange={e => setNewPassword(e.target.value)}
                      placeholder="Leave empty to auto-generate"
                      type={showPassword ? 'text' : 'password'}
                      style={inputStyle}
                    />
                  </div>
                  <button type="button" className={usersStyles.userSegmentActionPrimary} style={{ whiteSpace: 'nowrap' }} onClick={() => setNewPassword(generatePassword())}>Generate</button>
                  <button type="button" className={usersStyles.modalBackButton} style={{ whiteSpace: 'nowrap' }} onClick={() => setShowPassword(s => !s)}>{showPassword ? 'Hide' : 'Show'}</button>
                </div>
              </>
            )}

            {isEditMode && (
              <div style={{ ...fieldStyle, borderTop: '1px solid var(--ds-border)', paddingTop: 12, marginTop: 8 }}>
                <label style={labelStyle}>
                  Authorised jurisdictions {authJurInfo?.is_government && <span style={{ color: 'var(--ds-warning-text)', fontWeight: 600 }}>· required for government plans</span>}
                </label>
                <input value={authJur} onChange={e => setAuthJur(e.target.value)} placeholder="e.g. California, United States — comma separated; empty = no lock" style={inputStyle} />
                <div style={{ fontSize: 11.5, color: 'var(--admin-text-muted,var(--ds-text-muted))', marginTop: 4 }}>
                  When set, this replaces the user's own selection and the plan cap.
                  {authJurInfo && <> Currently sees: <strong>{authJurInfo.effective === null ? 'all jurisdictions' : authJurInfo.effective.length ? authJurInfo.effective.join(', ') : 'nothing'}</strong>.</>}
                  <button type="button" className={usersStyles.modalBackButton} style={{ marginLeft: 8, padding: '2px 8px', fontSize: 11.5 }} onClick={async () => {
                    const lockToken = window.localStorage.getItem('policyora_token') || '';
                    if (!lockToken || !editingUser) return;
                    try {
                      const api = await import('../../../api/adminApi');
                      const r = await api.setAuthorisedJurisdictions(lockToken, editingUser.id, authJur.split(',').map(x => x.trim()).filter(Boolean));
                      setAuthJurInfo(prev => ({ is_government: prev?.is_government ?? false, effective: r.effective }));
                      setAuthJur((r.authorised_jurisdictions || []).join(', '));
                    } catch (e) { alert(e instanceof Error ? e.message : 'Failed to save'); }
                  }}>Save lock</button>
                </div>
                <div style={{ marginTop: 10 }}>
                  <button type="button" className={usersStyles.modalBackButton} style={{ fontSize: 12 }} onClick={async () => {
                    const t = window.localStorage.getItem('policyora_token') || '';
                    if (!t || !editingUser || !window.confirm('Sign this user out of every device? They will need to sign in again.')) return;
                    try {
                      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL ?? ''}/api/v1/admin/users/${editingUser.id}/sessions/revoke`, { method: 'POST', headers: { Authorization: `Bearer ${t}` } });
                      const body = await res.json();
                      if (!res.ok) throw new Error(body?.detail || 'Failed');
                      alert(`Signed out of ${body.revoked} device${body.revoked === 1 ? '' : 's'}.`);
                    } catch (e) { alert(e instanceof Error ? e.message : 'Failed'); }
                  }}>Sign out of all devices</button>
                </div>
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
              <button type="button" className={usersStyles.modalBackButton} onClick={closeForm}>Cancel</button>
              <button type="button" className={usersStyles.userSegmentActionPrimary} onClick={isEditMode ? submitEdit : submitAdd}>
                {isEditMode ? 'Save changes' : (showEnterpriseFields ? 'Provision enterprise account' : 'Create user')}
              </button>
            </div>
          </div>
        </div>
      )}

      {!showAddModal && (
        <div style={{ background: 'var(--admin-surface,#fff)', borderRadius: 8, padding: 12 }}>
          {loading ? <p>Loading...</p> : error ? <p>{error}</p> : (
            <>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ textAlign: 'left', color: 'var(--admin-text-muted,var(--ds-text-muted))' }}>
                  <th style={{ padding: '8px 12px' }}>Email</th>
                  <th style={{ padding: '8px 12px' }}>Name</th>
                  <th style={{ padding: '8px 12px' }}>Organization</th>
                  <th style={{ padding: '8px 12px' }}>Role</th>
                  <th style={{ padding: '8px 12px' }}>Status</th>
                  <th style={{ padding: '8px 12px' }}>Plan / Validity</th>
                  <th style={{ padding: '8px 12px' }}>Joined</th>
                  <th style={{ padding: '8px 12px' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {users.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE).map(u => (
                  <tr key={u.id} style={{ borderTop: '1px solid var(--admin-border,var(--ds-border))' }}>
                    <td style={{ padding: '10px 12px' }}>{u.email}</td>
                    <td style={{ padding: '10px 12px' }}>{u.full_name || u.name || '-'}</td>
                    <td style={{ padding: '10px 12px' }}>{u.organization_name || u.organization_type || '-'}</td>
                    <td style={{ padding: '10px 12px' }}>{u.job_role || '-'}</td>
                    <td style={{ padding: '10px 12px' }}>{u.is_active ? 'Active' : 'Inactive'}</td>
                    <td style={{ padding: '10px 12px' }}>
                      {u.plan_tier ? (() => {
                        const status = (u.subscription_status || '').toLowerCase();
                        const exp = u.period_end || u.trial_ends_at;
                        const expStr = exp ? new Date(exp).toLocaleDateString() : null;
                        const expired = exp ? new Date(exp) < new Date() : false;
                        const bad = expired || ['expired', 'cancelled', 'past_due'].includes(status);
                        return (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <span style={{ fontWeight: 600, textTransform: 'capitalize' }}>{(u.plan_tier || '').replace(/_/g, ' ')}</span>
                            <span style={{ fontSize: 11, color: bad ? 'var(--ds-danger)' : 'var(--ds-success)' }}>
                              {status || 'unknown'}{expStr ? ` · ${expired ? 'expired' : 'until'} ${expStr}` : ''}
                            </span>
                          </div>
                        );
                      })() : <span style={{ color: 'var(--ds-text-faint)' }}>—</span>}
                    </td>
                    <td style={{ padding: '10px 12px' }}>{(u.joined_at || u.created_at || u.createdAt) ? new Date(u.joined_at || u.created_at || u.createdAt).toLocaleDateString() : '-'}</td>
                    <td style={{ padding: '10px 12px', display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {isEnterprise ? (
                        <>
                          {canEdit && (
                            <button
                              className={usersStyles.actionIcon}
                              style={{ color: 'var(--ds-link)', borderColor: 'var(--primary-300)' }}
                              onClick={() => handleEdit(u)}
                            >
                              Edit
                            </button>
                          )}
                          {canEdit && canGrantAccessForUser(u) && (
                            <button
                              className={usersStyles.actionIcon}
                              style={{ color: 'var(--ds-success-text)', borderColor: 'var(--ds-success)' }}
                              onClick={() => { setGrantUserId(u.id); setGrantDays('14'); }}
                            >
                              Grant Access
                            </button>
                          )}
                          {canEdit && (
                            <button
                              className={usersStyles.actionIcon}
                              onClick={() => handleResetPassword(u.id, u.email)}
                            >
                              Reset Password
                            </button>
                          )}
                          {canDelete && (
                            <button
                              className={usersStyles.actionIcon}
                              style={{ color: 'var(--ds-warning-text)', borderColor: 'var(--ds-warning)' }}
                              onClick={() => handleRevoke(u.id)}
                            >
                              Revoke
                            </button>
                          )}
                          {canDelete && (
                            <button
                              className={usersStyles.actionIcon}
                              data-testid={`block-user-${u.id}`}
                              style={u.is_active ? { color: 'var(--ds-danger-text)', borderColor: 'var(--ds-danger)' } : undefined}
                              onClick={() => handleBlockToggle(u.id, !!u.is_active)}
                            >
                              {u.is_active ? 'Block' : 'Unblock'}
                            </button>
                          )}
                          {canDelete && (
                            <button className={usersStyles.actionIcon} onClick={() => handleDelete(u.id)}>Delete</button>
                          )}
                        </>
                      ) : (
                        <>
                          {canEdit && canGrantAccessForUser(u) && (
                            <button
                              className={usersStyles.actionIcon}
                              style={{ color: 'var(--ds-success-text)', borderColor: 'var(--ds-success)' }}
                              onClick={() => { setGrantUserId(u.id); setGrantDays('14'); }}
                            >
                              Grant Access
                            </button>
                          )}
                          {canDelete && (
                            <button
                              className={usersStyles.actionIcon}
                              data-testid={`block-user-${u.id}`}
                              style={u.is_active ? { color: 'var(--ds-danger-text)', borderColor: 'var(--ds-danger)' } : undefined}
                              onClick={() => handleBlockToggle(u.id, !!u.is_active)}
                            >
                              {u.is_active ? 'Block' : 'Unblock'}
                            </button>
                          )}
                          {canDelete && (
                            <button className={usersStyles.actionIcon} onClick={() => handleDelete(u.id)}>Delete</button>
                          )}
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12 }}>
              <div style={{ color: 'var(--admin-text-muted,var(--ds-text-muted))' }}>
                Showing {users.length === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1} - {Math.min(currentPage * PAGE_SIZE, users.length)} of {users.length}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="button" className={usersStyles.modalBackButton} onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>← Previous</button>
                <button type="button" className={usersStyles.userSegmentActionPrimary} onClick={() => setCurrentPage(p => p * PAGE_SIZE < users.length ? p + 1 : p)} disabled={currentPage * PAGE_SIZE >= users.length}>Next →</button>
              </div>
            </div>
          </>
        )}
      </div>
      )}

      {/* Grant Access modal */}
      {grantUserId !== null && (
        <div className={usersStyles.modalOverlay} onClick={() => setGrantUserId(null)}>
          <div className={usersStyles.modalContent} onClick={e => e.stopPropagation()} style={{ maxWidth: 380, width: '100%', margin: '0 auto' }}>
            <h3 style={{ margin: '0 0 8px 0', fontSize: 16, fontWeight: 600 }}>Grant Access</h3>
            <p style={{ margin: '0 0 16px 0', fontSize: 13, color: 'var(--admin-text-muted,var(--ds-text-muted))' }}>
              Extend trial access for this user. Their subscription will be reactivated for the specified number of days.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 16 }}>
              <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--admin-text-muted,var(--ds-text-muted))' }}>Number of days</label>
              <input
                type="number"
                min={1}
                max={365}
                value={grantDays}
                onChange={e => setGrantDays(e.target.value)}
                style={{ padding: '8px 10px', borderRadius: 6, border: '1px solid var(--admin-border,var(--ds-border))', fontSize: 13, width: '100%', boxSizing: 'border-box' }}
              />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button className={usersStyles.modalBackButton} onClick={() => setGrantUserId(null)}>Cancel</button>
              <button className={usersStyles.userSegmentActionPrimary} onClick={handleGrantAccess}>Grant Access</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
